import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import TranscriptWithAudio from 'components/UI/transcriptWithAudio/transcriptWithAudio';

export const normalizeAudioPlaybackUrl = (raw) => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.trim();
    if (typeof raw === 'object' && typeof raw.url === 'string') return raw.url.trim();
    return '';
};

export const normalizeAudioMimeType = (mime) => {
    const base = (mime || '').split(';')[0].trim().toLowerCase();
    if (!base) return '';
    if (base === 'audio/mp3') return 'audio/mpeg';
    if (base === 'audio/x-m4a' || base === 'audio/m4a') return 'audio/mp4';
    return base;
};

export const getAudioExtensionFromMime = (mime) => {
    const normalized = normalizeAudioMimeType(mime);
    const extensionMap = {
        'audio/mpeg': '.mp3',
        'audio/wav': '.wav',
        'audio/mp4': '.m4a',
        'audio/aac': '.aac',
        'audio/ogg': '.ogg',
        'audio/webm': '.webm',
    };
    return extensionMap[normalized] || (isIOSAudioDevice() ? '.m4a' : '.webm');
};

export const detectAudioMimeFromBytes = (byteSample) => {
    const view = byteSample instanceof ArrayBuffer
        ? new Uint8Array(byteSample)
        : byteSample;
    if (!view || view.byteLength < 12) return '';
    if (view[0] === 0x1a && view[1] === 0x45 && view[2] === 0xdf && view[3] === 0xa3) {
        return 'audio/webm';
    }
    if (view[0] === 0x52 && view[1] === 0x49 && view[2] === 0x46 && view[3] === 0x46) {
        return 'audio/wav';
    }
    if (view[0] === 0x49 && view[1] === 0x44 && view[2] === 0x33) {
        return 'audio/mpeg';
    }
    if (view[4] === 0x66 && view[5] === 0x74 && view[6] === 0x79 && view[7] === 0x70) {
        return 'audio/mp4';
    }
    return '';
};

export const getAudioMimeTypeFromUrl = (url) => {
    const path = (url || '').split('?')[0].toLowerCase();
    const isActivitySubmission = path.includes('activity-submissions')
        || path.includes('activity%2fsubmissions');
    if (path.endsWith('.mp3')) return 'audio/mpeg';
    if (path.endsWith('.wav')) return 'audio/wav';
    if (path.endsWith('.m4a')) return 'audio/mp4';
    if (path.endsWith('.mp4')) return 'audio/mp4';
    if (path.endsWith('.aac')) return 'audio/aac';
    if (path.endsWith('.ogg')) return 'audio/ogg';
    if (path.endsWith('.webm')) {
        return isIOSAudioDevice() && isActivitySubmission ? 'audio/mp4' : 'audio/webm';
    }
    if (path.includes('.mp3')) return 'audio/mpeg';
    if (isActivitySubmission) {
        return isIOSAudioDevice() ? 'audio/mp4' : 'audio/webm';
    }
    if (path.includes('audio%2f') || path.includes('/audio/')) {
        return isIOSAudioDevice() ? 'audio/mp4' : 'audio/webm';
    }
    return '';
};

export const inferMimeTypeFromBlob = (blob, url, contentTypeHeader = '', byteSample = null) => {
    const sniffed = detectAudioMimeFromBytes(byteSample);
    if (sniffed) return sniffed;
    const headerType = normalizeAudioMimeType(contentTypeHeader);
    if (headerType && headerType.startsWith('audio/')) {
        return headerType;
    }
    const blobType = normalizeAudioMimeType(blob?.type);
    if (blobType && blobType.startsWith('audio/') && blobType !== 'application/octet-stream') {
        return blobType;
    }
    return getAudioMimeTypeFromUrl(url) || (isIOSAudioDevice() ? 'audio/mp4' : 'audio/webm');
};

export const prepareAudioBlobForPlayback = async (blob, url, contentTypeHeader = '') => {
    const sample = await blob.slice(0, 16).arrayBuffer();
    const resolvedMime = inferMimeTypeFromBlob(blob, url, contentTypeHeader, sample);
    const currentMime = normalizeAudioMimeType(blob.type);
    if (!currentMime || currentMime === 'application/octet-stream' || currentMime !== resolvedMime) {
        const buffer = await blob.arrayBuffer();
        return new Blob([buffer], { type: resolvedMime });
    }
    return blob;
};

export const fetchAudioForPlayback = async (url) => {
    const normalized = normalizeAudioPlaybackUrl(url);
    const response = await fetch(normalized);
    if (!response.ok) {
        throw new Error(`Audio fetch failed: ${response.status}`);
    }
    const blob = await response.blob();
    const typedBlob = await prepareAudioBlobForPlayback(
        blob,
        normalized,
        response.headers.get('content-type') || '',
    );
    return {
        blob: typedBlob,
        mimeType: normalizeAudioMimeType(typedBlob.type),
        objectUrl: URL.createObjectURL(typedBlob),
    };
};

export const urlNeedsBlobPlayback = (url) => {
    const normalized = normalizeAudioPlaybackUrl(url);
    if (!normalized) return false;
    if (isIOSAudioDevice()) return true;
    const path = normalized.split('?')[0].toLowerCase();
    const hasExtension = /\.(mp3|wav|m4a|aac|ogg|webm|mp4)(\?|$)/i.test(path);
    return !hasExtension
        || path.includes('activity-submissions')
        || path.includes('activity%2fsubmissions');
};

export const isIOSAudioDevice = () => {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const RetryButton = styled.button`
    margin-top: 6px;
    font-size: 12px;
    border: 1px solid #03989e;
    background: #fff;
    color: #03989e;
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
`;

const NativeAudioPlayer = ({
    url,
    onPlay,
    onRef,
    onCanPlayThrough,
    style = {},
    className = '',
    autoPlay = false,
}) => {
    const audioRef = useRef(null);
    const blobUrlRef = useRef(null);
    const fetchAttemptedRef = useRef(false);
    const mimeRetryIndexRef = useRef(0);
    const [playbackSrc, setPlaybackSrc] = useState('');
    const [hasError, setHasError] = useState(false);

    const normalizedUrl = useMemo(() => normalizeAudioPlaybackUrl(url), [url]);

    const setAudioRef = useCallback((el) => {
        audioRef.current = el;
        if (onRef) onRef(el);
    }, [onRef]);

    const applyPlaybackBlob = useCallback((objectUrl) => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = objectUrl;
        setPlaybackSrc(objectUrl);
        setHasError(false);
    }, []);

    const tryBlobFallback = useCallback(async (forcedMime = '') => {
        if (!normalizedUrl) return;
        if (fetchAttemptedRef.current && !forcedMime) return;
        if (!forcedMime) fetchAttemptedRef.current = true;
        try {
            const response = await fetch(normalizedUrl);
            if (!response.ok) {
                setHasError(true);
                setPlaybackSrc(normalizedUrl);
                return;
            }
            const rawBlob = await response.blob();
            const typedBlob = forcedMime
                ? new Blob([await rawBlob.arrayBuffer()], { type: forcedMime })
                : await prepareAudioBlobForPlayback(
                    rawBlob,
                    normalizedUrl,
                    response.headers.get('content-type') || '',
                );
            applyPlaybackBlob(URL.createObjectURL(typedBlob));
        } catch {
            setHasError(true);
            setPlaybackSrc(normalizedUrl);
        }
    }, [normalizedUrl, applyPlaybackBlob]);

    useEffect(() => {
        fetchAttemptedRef.current = false;
        mimeRetryIndexRef.current = 0;
        setHasError(false);
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        if (!normalizedUrl) {
            setPlaybackSrc('');
            return;
        }
        if (isIOSAudioDevice()) {
            tryBlobFallback();
        } else {
            setPlaybackSrc(normalizedUrl);
        }
    }, [normalizedUrl, tryBlobFallback]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el || !playbackSrc) return;
        el.load();
    }, [playbackSrc]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el || !autoPlay) return;
        const handleCanPlay = () => {
            el.play().catch(() => {});
            if (onCanPlayThrough) onCanPlayThrough(el);
        };
        el.addEventListener('canplaythrough', handleCanPlay, { once: true });
        return () => el.removeEventListener('canplaythrough', handleCanPlay);
    }, [autoPlay, playbackSrc, onCanPlayThrough]);

    useEffect(() => () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    }, []);

    const handleError = () => {
        if (!fetchAttemptedRef.current) {
            tryBlobFallback();
            return;
        }
        const retryMimes = isIOSAudioDevice()
            ? ['audio/mp4', 'audio/mpeg', 'audio/webm']
            : ['audio/webm', 'audio/mp4', 'audio/mpeg'];
        const nextMime = retryMimes[mimeRetryIndexRef.current];
        if (nextMime) {
            mimeRetryIndexRef.current += 1;
            tryBlobFallback(nextMime);
            return;
        }
        setHasError(true);
    };

    if (!normalizedUrl || !playbackSrc) return null;

    return (
        <>
            <audio
                ref={setAudioRef}
                className={className}
                controls
                controlsList="nodownload"
                playsInline
                preload="auto"
                src={playbackSrc}
                onPlay={(e) => onPlay?.(e.currentTarget)}
                onError={handleError}
                onCanPlayThrough={() => onCanPlayThrough?.(audioRef.current)}
                style={{
                    width: '100%',
                    height: '40px',
                    marginBottom: '8px',
                    ...style,
                }}
            />
            {hasError ? (
                <RetryButton
                    type="button"
                    onClick={() => {
                        fetchAttemptedRef.current = false;
                        mimeRetryIndexRef.current = 0;
                        tryBlobFallback();
                    }}
                >
                    Retry audio
                </RetryButton>
            ) : null}
        </>
    );
};

export const AssignmentAudioPlayer = ({
    url,
    audioKey,
    audioPlayersRef,
    pauseAllOtherAudioPlayers,
    title = '',
    style = {},
    withTranscript = true,
    autoPlay = false,
    onCanPlayThrough,
}) => {
    const normalizedUrl = normalizeAudioPlaybackUrl(url);
    if (!normalizedUrl) return null;

    if (!audioPlayersRef.current.has(audioKey)) {
        audioPlayersRef.current.set(audioKey, { current: null, hasAutoPlayed: false });
    }
    const playerEntry = audioPlayersRef.current.get(audioKey);

    const handleRef = (el) => {
        playerEntry.current = el;
    };

    const handlePlay = () => {
        if (typeof pauseAllOtherAudioPlayers === 'function') {
            pauseAllOtherAudioPlayers(playerEntry);
        }
    };

    const handleCanPlayThrough = (el) => {
        if (autoPlay && !playerEntry.hasAutoPlayed && el) {
            el.play().catch(() => {});
            playerEntry.hasAutoPlayed = true;
        }
        if (onCanPlayThrough) onCanPlayThrough(el);
    };

    const player = (
        <NativeAudioPlayer
            url={normalizedUrl}
            onRef={handleRef}
            onPlay={handlePlay}
            onCanPlayThrough={handleCanPlayThrough}
            style={style}
            autoPlay={autoPlay}
        />
    );

    if (!withTranscript) return player;

    return (
        <TranscriptWithAudio audioUrl={normalizedUrl} title={title}>
            {player}
        </TranscriptWithAudio>
    );
};

export default NativeAudioPlayer;
